import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function FuelGaugeComparison() {
  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const cxLeft = width * 0.25;
    const cxRight = width * 0.75;
    const cyStars = height / 2 - 40; // centered nicely

    // Draw central divider line (dashed for modern styling)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(width / 2, 40);
    ctx.lineTo(width / 2, height - 30);
    ctx.stroke();
    ctx.setLineDash([]);

    // 1. Calculations for Blue Supergiant (Left Half)
    const blueDrainRate = 1.5; // percent per second
    const blueCycleDuration = 100 / blueDrainRate; // ~66.67s
    const blueDeadDuration = 2.0; // 2s dead state
    const blueTotalCycle = blueCycleDuration + blueDeadDuration;

    const currentCycleTime = totalTime % blueTotalCycle;
    let blueFuel = 100;
    let isBlueDead = false;

    if (currentCycleTime < blueCycleDuration) {
      blueFuel = 100 - currentCycleTime * blueDrainRate;
    } else {
      blueFuel = 0;
      isBlueDead = true;
    }

    // 2. Calculations for Red Dwarf (Right Half)
    const redDrainRate = 0.003; // percent per second
    const redFuel = Math.max(0, 100 - totalTime * redDrainRate);

    // Helper to draw a fuel tank
    const drawFuelTank = (x: number, y: number, fuel: number, isTankDead: boolean) => {
      const w = 20; // 20px wide
      const h = 100; // 100px tall

      // Track background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 4);
      ctx.fill();

      // Outer frame
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 4);
      ctx.stroke();

      // Filled fuel level
      const f = fuel / 100;
      if (f > 0) {
        // Interpolate color: Blue (full, rgb(59,130,246)) -> Red (empty, rgb(239,68,68))
        const r = Math.round(239 + f * (59 - 239));
        const g = Math.round(68 + f * (130 - 68));
        const b = Math.round(68 + f * (246 - 68));

        ctx.fillStyle = isTankDead ? '#EF4444' : `rgb(${r}, ${g}, ${b})`;
        const fillH = f * h;
        ctx.beginPath();
        ctx.roundRect(x + 2, y + h - fillH + 2, w - 4, fillH - 4, Math.min(2, fillH / 2));
        ctx.fill();
      }

      // Label below fuel tank
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${fuel.toFixed(1)}%`, x + w / 2, y + h + 18);

      // Label above fuel tank
      ctx.font = 'bold 9px sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fillText('FUEL RESERVES', x + w / 2, y - 8);
    };

    // --- LEFT HALF: Blue Supergiant ---
    // Star glow
    const blueRadius = 50; // exact radius 50
    const starOpacity = isBlueDead ? 0.15 : 0.85;
    const blueGlow = ctx.createRadialGradient(cxLeft, cyStars, 0, cxLeft, cyStars, blueRadius * 1.6);
    blueGlow.addColorStop(0, `rgba(96, 165, 250, ${starOpacity})`);
    blueGlow.addColorStop(0.4, `rgba(96, 165, 250, ${starOpacity * 0.5})`);
    blueGlow.addColorStop(1, 'rgba(96, 165, 250, 0)');
    ctx.fillStyle = blueGlow;
    ctx.beginPath();
    ctx.arc(cxLeft, cyStars, blueRadius * 1.6, 0, Math.PI * 2);
    ctx.fill();

    // Star core
    ctx.fillStyle = isBlueDead ? 'rgba(255, 255, 255, 0.15)' : '#FFFFFF';
    ctx.beginPath();
    ctx.arc(cxLeft, cyStars, blueRadius * 0.9, 0, Math.PI * 2);
    ctx.fill();

    if (isBlueDead) {
      ctx.fillStyle = '#EF4444';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('DEAD ☠', cxLeft, cyStars + 6);
    }

    // Labels left half
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Blue Supergiant (20M☉)', cxLeft, cyStars - 75);

    ctx.fillStyle = '#F87171'; // Red-orange
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('Burns: 10,000× faster', cxLeft, cyStars - 58);

    drawFuelTank(cxLeft - 10, cyStars + 65, blueFuel, isBlueDead);

    // --- RIGHT HALF: Red Dwarf ---
    // Star glow
    const redRadius = 20; // exact radius 20
    const redGlow = ctx.createRadialGradient(cxRight, cyStars, 0, cxRight, cyStars, redRadius * 1.6);
    redGlow.addColorStop(0, 'rgba(239, 68, 68, 0.6)');
    redGlow.addColorStop(1, 'rgba(239, 68, 68, 0)');
    ctx.fillStyle = redGlow;
    ctx.beginPath();
    ctx.arc(cxRight, cyStars, redRadius * 1.6, 0, Math.PI * 2);
    ctx.fill();

    // Star core
    ctx.fillStyle = '#FF8A65'; // Orange-red
    ctx.beginPath();
    ctx.arc(cxRight, cyStars, redRadius * 0.95, 0, Math.PI * 2);
    ctx.fill();

    // Labels right half
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Red Dwarf (0.1M☉)', cxRight, cyStars - 75);

    ctx.fillStyle = '#34D399'; // Green
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('Sips fuel slowly', cxRight, cyStars - 58);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '11px sans-serif';
    ctx.fillText('Would take: ~10 Trillion Years to empty', cxRight, cyStars + 65 + 138);

    drawFuelTank(cxRight - 10, cyStars + 65, redFuel, false);

    // --- HUD: Simulation Time (Top Centre) ---
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`Simulation Time: ${totalTime.toFixed(1)}s`, width / 2, 28);

    // Top-Left Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Lesson: Astro G8 L05 - Fuel Consumption Rates', 12, 24);
  }, []);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
